import type { AssetResource } from '@/hook/use_app_assets';
import {
  get_portfolio_navigation_item,
  scroll_to_navigation_item,
} from '@/lib/portfolio-navigation';
import type {
  AssetIndexBySequenceNumber,
  PortfolioNavigationSectionId,
} from '@/lib/portfolio-navigation';

type AssetHotspotsProps = {
  asset_index_by_sequence_number: AssetIndexBySequenceNumber;
  asset_resource: AssetResource;
};

type BaseAssetHotspotConfig = {
  aria_label: string;
  height_percent: number;
  left_percent: number;
  target_asset_sequence_number: number;
  top_percent: number;
  width_percent: number;
};

type AssetExternalLinkHotspotConfig = BaseAssetHotspotConfig & {
  href: string;
  kind: 'external-link';
};

export type AssetNavigationHotspotConfig = BaseAssetHotspotConfig & {
  kind: 'portfolio-navigation';
  navigation_section_id: PortfolioNavigationSectionId;
};

type AssetHotspotRenderConfig =
  | AssetExternalLinkHotspotConfig
  | AssetNavigationHotspotConfig;

type NavigationHotspotConfigInput = Omit<
  AssetNavigationHotspotConfig,
  'kind' | 'target_asset_sequence_number'
>;

const ASSET_HOTSPOT_LINK_CLASS_NAME =
  'absolute z-50 block cursor-pointer bg-transparent outline-none focus-visible:ring-2 focus-visible:ring-[#26221c] focus-visible:ring-offset-2';
const ASSET_NAVIGATION_HOTSPOT_BUTTON_CLASS_NAME =
  'absolute z-50 block cursor-pointer bg-transparent p-0 outline-none focus-visible:ring-2 focus-visible:ring-[#26221c] focus-visible:ring-offset-2';
const ASSET_HOTSPOT_ANCHOR_TARGET = '_blank';
const BRAND_DESIGN_HOTSPOT_STYLE = {
  aria_label: '跳转到品牌设计',
  navigation_section_id: 'brand-design',
} satisfies Omit<
  NavigationHotspotConfigInput,
  'height_percent' | 'left_percent' | 'top_percent' | 'width_percent'
>;
const PACKAGING_DESIGN_HOTSPOT_STYLE = {
  aria_label: '跳转到包装设计',
  navigation_section_id: 'packaging-design',
} satisfies Omit<
  NavigationHotspotConfigInput,
  'height_percent' | 'left_percent' | 'top_percent' | 'width_percent'
>;
const OTHER_DESIGN_HOTSPOT_STYLE = {
  aria_label: '跳转到其他设计',
  navigation_section_id: 'other-design',
} satisfies Omit<
  NavigationHotspotConfigInput,
  'height_percent' | 'left_percent' | 'top_percent' | 'width_percent'
>;
const ECOMMERCE_DESIGN_HOTSPOT_STYLE = {
  aria_label: '跳转到电商设计',
  navigation_section_id: 'ecommerce-design',
} satisfies Omit<
  NavigationHotspotConfigInput,
  'height_percent' | 'left_percent' | 'top_percent' | 'width_percent'
>;
const MODIAN_QR_CODE_HOTSPOT_CONFIG: AssetExternalLinkHotspotConfig = {
  aria_label: '查看未及四季后花园动画项目',
  height_percent: 20.5,
  href: 'https://zhongchou.modian.com/item/107315.html',
  kind: 'external-link',
  left_percent: 1.8,
  target_asset_sequence_number: 71,
  top_percent: 72.3,
  width_percent: 13.2,
};
const PORTFOLIO_NAVIGATION_HOTSPOT_CONFIGS_BY_SEQUENCE_NUMBER = new Map<
  number,
  readonly AssetNavigationHotspotConfig[]
>([
  [
    4,
    [
      create_navigation_hotspot_config({
        ...BRAND_DESIGN_HOTSPOT_STYLE,
        height_percent: 35.7,
        left_percent: 4.8,
        target_asset_sequence_number: 4,
        top_percent: 24.1,
        width_percent: 24.5,
      }),
      create_navigation_hotspot_config({
        ...PACKAGING_DESIGN_HOTSPOT_STYLE,
        height_percent: 21.2,
        left_percent: 32.4,
        target_asset_sequence_number: 4,
        top_percent: 31.4,
        width_percent: 17.0,
      }),
      create_navigation_hotspot_config({
        ...OTHER_DESIGN_HOTSPOT_STYLE,
        height_percent: 21.2,
        left_percent: 55.1,
        target_asset_sequence_number: 4,
        top_percent: 31.4,
        width_percent: 17.1,
      }),
      create_navigation_hotspot_config({
        ...ECOMMERCE_DESIGN_HOTSPOT_STYLE,
        height_percent: 21.2,
        left_percent: 77.9,
        target_asset_sequence_number: 4,
        top_percent: 31.4,
        width_percent: 17.0,
      }),
    ],
  ],
  [
    44,
    [
      create_navigation_hotspot_config({
        ...BRAND_DESIGN_HOTSPOT_STYLE,
        height_percent: 21.0,
        left_percent: 6.6,
        target_asset_sequence_number: 44,
        top_percent: 25.9,
        width_percent: 17.0,
      }),
      create_navigation_hotspot_config({
        ...PACKAGING_DESIGN_HOTSPOT_STYLE,
        height_percent: 35.5,
        left_percent: 26.6,
        target_asset_sequence_number: 44,
        top_percent: 18.5,
        width_percent: 24.4,
      }),
      create_navigation_hotspot_config({
        ...OTHER_DESIGN_HOTSPOT_STYLE,
        height_percent: 21.0,
        left_percent: 54.3,
        target_asset_sequence_number: 44,
        top_percent: 25.9,
        width_percent: 17.0,
      }),
      create_navigation_hotspot_config({
        ...ECOMMERCE_DESIGN_HOTSPOT_STYLE,
        height_percent: 21.0,
        left_percent: 76.7,
        target_asset_sequence_number: 44,
        top_percent: 25.9,
        width_percent: 17.0,
      }),
    ],
  ],
  [
    62,
    [
      create_navigation_hotspot_config({
        ...BRAND_DESIGN_HOTSPOT_STYLE,
        height_percent: 21.0,
        left_percent: 6.5,
        target_asset_sequence_number: 62,
        top_percent: 29.0,
        width_percent: 17.0,
      }),
      create_navigation_hotspot_config({
        ...PACKAGING_DESIGN_HOTSPOT_STYLE,
        height_percent: 21.0,
        left_percent: 28.9,
        target_asset_sequence_number: 62,
        top_percent: 29.0,
        width_percent: 17.0,
      }),
      create_navigation_hotspot_config({
        ...OTHER_DESIGN_HOTSPOT_STYLE,
        height_percent: 35.6,
        left_percent: 49.0,
        target_asset_sequence_number: 62,
        top_percent: 21.8,
        width_percent: 24.5,
      }),
      create_navigation_hotspot_config({
        ...ECOMMERCE_DESIGN_HOTSPOT_STYLE,
        height_percent: 21.0,
        left_percent: 76.5,
        target_asset_sequence_number: 62,
        top_percent: 29.0,
        width_percent: 17.0,
      }),
    ],
  ],
  [
    82,
    [
      create_navigation_hotspot_config({
        ...BRAND_DESIGN_HOTSPOT_STYLE,
        height_percent: 21.0,
        left_percent: 5.5,
        target_asset_sequence_number: 82,
        top_percent: 23.3,
        width_percent: 17.0,
      }),
      create_navigation_hotspot_config({
        ...PACKAGING_DESIGN_HOTSPOT_STYLE,
        height_percent: 21.0,
        left_percent: 28.2,
        target_asset_sequence_number: 82,
        top_percent: 23.3,
        width_percent: 17.0,
      }),
      create_navigation_hotspot_config({
        ...OTHER_DESIGN_HOTSPOT_STYLE,
        height_percent: 21.0,
        left_percent: 50.9,
        target_asset_sequence_number: 82,
        top_percent: 23.3,
        width_percent: 17.0,
      }),
      create_navigation_hotspot_config({
        ...ECOMMERCE_DESIGN_HOTSPOT_STYLE,
        height_percent: 35.5,
        left_percent: 70.8,
        target_asset_sequence_number: 82,
        top_percent: 16.1,
        width_percent: 24.5,
      }),
    ],
  ],
]);

export function AssetHotspots({
  asset_index_by_sequence_number,
  asset_resource,
}: AssetHotspotsProps) {
  return get_asset_hotspot_configs_by_sequence_number(
    asset_resource.asset_sequence_number,
  ).map(asset_hotspot_config => (
    <AssetHotspot
      key={`${asset_resource.file_name}-${asset_hotspot_config.aria_label}`}
      asset_hotspot_config={asset_hotspot_config}
      asset_index_by_sequence_number={asset_index_by_sequence_number}
    />
  ));
}

export function get_asset_hotspot_anchor_target() {
  return ASSET_HOTSPOT_ANCHOR_TARGET;
}

export function get_asset_hotspot_configs_by_sequence_number(
  asset_sequence_number: number,
): readonly AssetHotspotRenderConfig[] {
  const navigation_hotspot_configs =
    PORTFOLIO_NAVIGATION_HOTSPOT_CONFIGS_BY_SEQUENCE_NUMBER.get(
      asset_sequence_number,
    );

  if (navigation_hotspot_configs) {
    return navigation_hotspot_configs;
  }

  if (
    asset_sequence_number ===
    MODIAN_QR_CODE_HOTSPOT_CONFIG.target_asset_sequence_number
  ) {
    return [MODIAN_QR_CODE_HOTSPOT_CONFIG];
  }

  return [];
}

function AssetHotspot({
  asset_hotspot_config,
  asset_index_by_sequence_number,
}: {
  asset_hotspot_config: AssetHotspotRenderConfig;
  asset_index_by_sequence_number: AssetIndexBySequenceNumber;
}) {
  if (asset_hotspot_config.kind === 'portfolio-navigation') {
    return (
      <NavigationHotspotButton
        asset_hotspot_config={asset_hotspot_config}
        asset_index_by_sequence_number={asset_index_by_sequence_number}
      />
    );
  }

  return (
    <a
      aria-label={asset_hotspot_config.aria_label}
      className={ASSET_HOTSPOT_LINK_CLASS_NAME}
      href={asset_hotspot_config.href}
      rel="noreferrer"
      style={get_hotspot_position_style(asset_hotspot_config)}
      target={get_asset_hotspot_anchor_target()}
    />
  );
}

function NavigationHotspotButton({
  asset_hotspot_config,
  asset_index_by_sequence_number,
}: {
  asset_hotspot_config: AssetNavigationHotspotConfig;
  asset_index_by_sequence_number: AssetIndexBySequenceNumber;
}) {
  const handle_navigation_hotspot_click = () => {
    scroll_to_navigation_item({
      asset_index_by_sequence_number,
      navigation_item: get_portfolio_navigation_item(
        asset_hotspot_config.navigation_section_id,
      ),
    });
  };

  return (
    <button
      aria-label={asset_hotspot_config.aria_label}
      className={ASSET_NAVIGATION_HOTSPOT_BUTTON_CLASS_NAME}
      onClick={handle_navigation_hotspot_click}
      style={get_hotspot_position_style(asset_hotspot_config)}
      type="button"
    />
  );
}

function get_hotspot_position_style(asset_hotspot_config: BaseAssetHotspotConfig) {
  return {
    height: `${asset_hotspot_config.height_percent}%`,
    left: `${asset_hotspot_config.left_percent}%`,
    top: `${asset_hotspot_config.top_percent}%`,
    width: `${asset_hotspot_config.width_percent}%`,
  };
}

function create_navigation_hotspot_config({
  target_asset_sequence_number,
  ...navigation_hotspot_config
}: NavigationHotspotConfigInput & {
  target_asset_sequence_number: number;
}): AssetNavigationHotspotConfig {
  return {
    ...navigation_hotspot_config,
    kind: 'portfolio-navigation',
    target_asset_sequence_number,
  };
}
